import { createFileRoute } from '@tanstack/react-router';
import type { IconName } from 'lucide-react/dynamic';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { CopyText } from '@/components/ui/copy-text';
import { Form, FormContent, FormFooter } from '@/components/ui/form';
import { Icon } from '@/components/ui/icon';
import { ContentLoader } from '@/components/ui/loaders';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { ScreenForm } from '@/lib/screen-renderer';
import { useDynamicDetail, useDynamicUpdate } from '@/services/dynamic-resource';
import { useScreenByResource } from '@/services/screens';
import type { ValidationError } from '@/types/api/validation-error';

export const Route = createFileRoute('/_app/$resource/$id/')({
  head: () => meta({ label: 'Editar' }),
  component: RouteComponent,
});

function RouteComponent() {
  const { resource, id } = Route.useParams();
  const router = useRouter();

  const { data: screen, isLoading: loadingScreen } = useScreenByResource(resource);
  const { data, isLoading: loadingData } = useDynamicDetail(resource, id);
  const {
    mutate: save,
    error,
    isSuccess,
    isPending: isSubmitting,
  } = useDynamicUpdate(resource);

  const form = useForm<Record<string, any>>({
    shouldUseNativeValidation: false,
    defaultValues: {},
  });

  // `data` chega depois da montagem (a query ainda está em andamento no primeiro render) —
  // `defaultValues` do useForm só é lido uma vez, então sem isso o form ficava vazio na
  // primeira visita à página (só preenchia se a query já estivesse em cache de uma visita anterior).
  useEffect(() => {
    if (data) form.reset(data as Record<string, any>);
  }, [data, form]);

  const handleSubmit = (values: Record<string, any>) => save({ id, data: values });

  useEffect(() => {
    if (isSuccess) router.back();
  }, [isSuccess, router]);

  useEffect(() => {
    if (error && (error as any).status === 422) {
      const { errors } = (error as any).value as ValidationError<Record<string, any>>;

      if (errors) {
        Object.entries(errors).forEach(([key, message]) => {
          form.setError(key, { message: message as string });
        });
      }
    }
  }, [error, form]);

  if (loadingScreen || !screen || loadingData || !data) return <ContentLoader show />;

  const label = `Editar ${screen.label}`;
  const shortId = id?.slice(0, 8);

  return (
    <Content
      breadcrumbs={{
        title: (
          <>
            {label} <CopyText className="ml-2" value={id} label={`${shortId}...`} />
          </>
        ),
        icon: screen.icon as IconName,
        items: [
          { label: 'Listagem', route: `/${resource}` },
          { label: 'Editar', active: true },
        ],
      }}
    >
      <Form
        id="form"
        title={`Dados do ${screen.label}`}
        description={`Informe os dados do ${screen.label.toLowerCase()}.`}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormContent>
          <ScreenForm
            fields={screen.layout.fields}
            control={form.control}
            mode="edit"
            resource={resource}
            recordId={id}
          />
        </FormContent>
        <FormFooter>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="sand"
            size="lg"
            form="form"
            className="flex items-center gap-2"
          >
            <Icon name="save" />
            Salvar
          </Button>
        </FormFooter>
      </Form>
    </Content>
  );
}
